import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  type PaymentMethod,
  type StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import {
  capitalizeFirstLetter,
  type IconName,
  useNoticeBannersStackContext,
} from '@sg/garnish';

import { type UseStripePaymentProps } from '../../types';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const { isEnabled, paymentMethods, onConfirm } = props;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const errorMessage = formatMessage(messages.error);
  const convertPaymentType = usePaymentTypeConversion();

  const notifyStripeError = useCallback(
    (message = errorMessage) => {
      addNoticeBanner({ text: message, palette: 'caution' });
    },
    [errorMessage, addNoticeBanner],
  );

  const stripe = useStripe();
  const elements = useElements();

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] =
    useState<IconName>('IconCreditCard');

  const createPaymentMethod = useCallback(async () => {
    if (!stripe || !elements || !isEnabled) {
      notifyStripeError();

      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      notifyStripeError(submitError.message);

      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
    });

    if (error) {
      notifyStripeError(error.message);

      return;
    }

    setCreatedPaymentMethod(paymentMethod.id);

    return paymentMethod;
  }, [elements, isEnabled, stripe, notifyStripeError]);

  useEffect(() => {
    if (!stripe || !elements || !isEnabled) return;

    const paymentElement = elements?.getElement('payment');

    if (paymentElement) {
      const handleChange = getPaymentMethodChangeHandler({
        paymentMethods,
        formatMessage,
        convertPaymentType,
        setCreatedPaymentMethod,
        setSelectedPaymentMethod,
        setSelectedPaymentIcon,
        createPaymentMethod,
      });

      paymentElement.on('change', handleChange);

      return () => {
        paymentElement.off('change', handleChange);
      };
    }
  }, [
    stripe,
    elements,
    isEnabled,
    paymentMethods,
    convertPaymentType,
    createPaymentMethod,
    formatMessage,
  ]);

  const confirm = useCallback(async () => {
    if (createdPaymentMethod) {
      onConfirm(createdPaymentMethod);

      return;
    }

    const paymentMethod = await createPaymentMethod();

    if (!paymentMethod) return;

    onConfirm(paymentMethod.id);
  }, [createdPaymentMethod, createPaymentMethod, onConfirm]);

  return {
    isStripeReady: Boolean(stripe && elements),
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirm,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const usePaymentTypeConversion = (): ((
  type: string,
) => PaymentTypeConversion) => {
  const { formatMessage } = useIntl();

  return useCallback(
    (type: string) => {
      switch (type) {
        case 'card': {
          return {
            label: '',
            icon: 'IconCreditCard',
          };
        }

        case 'apple_pay': {
          return {
            label: formatMessage(messages.applePay),
            icon: 'IconApplePayPayment',
          };
        }

        case 'google_pay': {
          return {
            label: formatMessage(messages.googlePay),
            icon: 'IconGooglePay',
          };
        }

        default: {
          return {
            label: formatMessage(messages.paymentMethod),
            icon: 'IconCreditCard',
          };
        }
      }
    },
    [formatMessage],
  );
};

/**
 * Handles changes to the payment method, setting state as needed.
 */
const getPaymentMethodChangeHandler = (params: PaymentMethodChangeHandler) => {
  const {
    paymentMethods,
    formatMessage,
    convertPaymentType,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  } = params;

  return async (event: StripePaymentElementChangeEvent) => {
    setCreatedPaymentMethod('');

    const id = event.value.payment_method?.id;

    const savedPaymentMethod = paymentMethods.find((pm) => pm.id === id);
    const savedDescription = savedPaymentMethod?.description;
    const savedPaymentMethodLabel = removeEnding(savedDescription);
    const savedPaymentMethodType = savedPaymentMethod?.cardType;

    // Customer selected a card that is already saved in Stripe.
    if (savedPaymentMethodLabel && savedPaymentMethodType) {
      setSelectedPaymentMethod(savedPaymentMethodLabel);
      setSelectedPaymentIcon(CREDIT_CARDS_ICONS[savedPaymentMethodType]);

      return;
    }

    // Customer is adding a new card that is already saved in Stripe.
    if (event.value.type === 'card' && event.complete) {
      const paymentMethod = await createPaymentMethod();

      if (!paymentMethod?.card) return;

      const { brand, last4 } = paymentMethod.card;

      const label = formatMessage(messages.newPaymentMethod, {
        brand: capitalizeFirstLetter(brand),
        last4,
      });

      setSelectedPaymentMethod(label);
      setSelectedPaymentIcon(CREDIT_CARDS_ICONS[brand.toUpperCase()]);

      return;
    }

    const { label, icon } = convertPaymentType(event.value.type);

    setSelectedPaymentMethod(label);
    setSelectedPaymentIcon(icon);
  };
};

const removeEnding = (name?: string | null) => {
  return name?.replaceAll('ending in', '');
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentTypeConversion = {
  label: string;
  icon: IconName;
};

type PaymentMethodChangeHandler = {
  paymentMethods: UseStripePaymentProps['paymentMethods'];
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  convertPaymentType: ReturnType<typeof usePaymentTypeConversion>;
  setCreatedPaymentMethod: (value: string) => void;
  setSelectedPaymentMethod: (value: string) => void;
  setSelectedPaymentIcon: (value: IconName) => void;
  createPaymentMethod: () => Promise<PaymentMethod | undefined>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CREDIT_CARDS_ICONS: Record<string, IconName> = {
  VISA: 'IconCcVisa',
  MASTER_CARD: 'IconCcMasterCard',
  DISCOVER: 'IconCcDiscover',
  AMEX: 'IconCcAmex',
  AMERICAN_EXPRESS: 'IconCcAmex',
  UNSUPPORTED: 'IconCreditCard',
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  paymentMethod: {
    defaultMessage: 'Payment Method',
    description: 'Stripe Payment | Payment Method',
  },
  applePay: {
    defaultMessage: 'Apple Pay',
    description: 'Stripe Payment | Apple Pay',
  },
  googlePay: {
    defaultMessage: 'Google Pay',
    description: 'Stripe Payment | Google Pay',
  },
  newPaymentMethod: {
    defaultMessage: '{brand} ending in {last4}',
    description: 'Stripe Payment | New Payment Method',
  },
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Stripe Payment | Unknown Error',
  },
});
